import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  Stack,
  alpha,
  useTheme
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Calculate as CalculateIcon,
  Handshake as RenegotiateIcon,
  CheckCircle,
  AccountBalanceWallet
} from '@mui/icons-material';
import invoiceService from '../../../services/invoiceService';
import GatewaySelect from '../../../components/selects/GatewaySelect';
import ContractsActiveSelect from '../../../components/selects/ContractsActiveSelect';
import { formatarData, formatarValor, formatarValorOnInput, parsearMoeda } from '../../../utils/formatToView';
import { Gateway } from '../../../services/gatewayService';

export interface RenegociarCobrancaModalProps {
  open: boolean;
  onClose: () => void;
  invoiceIds: number[];
  cobrancaId?: number | null;
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

interface InvoiceData {
  id: number;
  cliente_id: number;
  contrato_id?: number;
  valor: number;
  acrescimo: number;
  desconto: number;
  data_vencimento: string;
  data_emissao: string;
  descricao: string;
  mes_referencia?: string;
  data_inicial?: string | null;
  data_final?: string | null;
  gateway_id?: number;
  gateway?: { id: number; nome: string } | null;
}

const getCurrentDate = (): string => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

const formatarMoeda = (valor: number): string =>
  valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const calcularJurosMulta = (
  valor: number,
  dataVencimento: string,
  dataNovaVencimento: string,
  jurosPercent: number,
  multaPercent: number
): { juros: number; multa: number; dias: number } => {
  const venc = new Date(dataVencimento + 'T00:00:00');
  const nova = new Date(dataNovaVencimento + 'T00:00:00');
  const dias = Math.max(0, Math.floor((nova.getTime() - venc.getTime()) / (1000 * 60 * 60 * 24)));
  if (dias <= 0) return { juros: 0, multa: 0, dias: 0 };
  const multa = valor * (multaPercent / 100);
  const jurosDia = (jurosPercent / 100) / 30; // Juros calculado proporcionalmente ao mês comercial de 30 dias
  const juros = valor * jurosDia * dias;
  return { juros, multa, dias };
};

const RenegociarCobrancaModal: React.FC<RenegociarCobrancaModalProps> = ({
  open,
  onClose,
  invoiceIds,
  cobrancaId,
  onSuccess,
  onError,
}) => {
  const theme = useTheme();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<InvoiceData[]>([]);
  const [configuracao, setConfiguracao] = useState<{ juros: number; multa: number }>({ juros: 0, multa: 0 });
  const [clientId, setClientId] = useState<number | null>(null);

  const [dataVencimento, setDataVencimento] = useState(getCurrentDate());
  const [valorParcelas, setValorParcelas] = useState<number>(0);
  const [valorParcelasFormatado, setValorParcelasFormatado] = useState('0,00');
  const [acrescimo, setAcrescimo] = useState<number>(0);
  const [acrescimoFormatado, setAcrescimoFormatado] = useState('0,00');
  const [desconto, setDesconto] = useState<number>(0);
  const [descontoFormatado, setDescontoFormatado] = useState('0,00');
  const [contratoId, setContratoId] = useState<number | null>(null);
  const [contratoSelectDisabled, setContratoSelectDisabled] = useState(false);
  const [gatewayId, setGatewayId] = useState<number | null>(null);
  const [selectedGateway, setSelectedGateway] = useState<Gateway | null>(null);

  const valorTotal = valorParcelas + acrescimo - desconto;

  useEffect(() => {
    if (open && invoiceIds.length > 0) {
      loadData();
    }
  }, [open, invoiceIds]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await invoiceService.getRenegociarDados(invoiceIds);
      if (response.success && response.data) {
        setInvoices(response.data.invoices);
        setConfiguracao(response.data.configuracao);
        const somaValor = response.data.invoices.reduce(
          (acc: number, inv: InvoiceData) => acc + inv.valor + (inv.acrescimo || 0) - (inv.desconto || 0),
          0
        );
        setValorParcelas(somaValor);
        setValorParcelasFormatado(formatarMoeda(somaValor));
        if (response.data.invoices[0]) {
          setClientId(response.data.invoices[0].cliente_id);
        }
        const contratoIds = (response.data.invoices as InvoiceData[])
          .map((inv) => inv.contrato_id)
          .filter((id): id is number => id != null && id > 0);
        const todasTemContrato = contratoIds.length === response.data.invoices.length;
        const uniqueContratos = [...new Set(contratoIds)];
        if (todasTemContrato && uniqueContratos.length === 1) {
          setContratoId(uniqueContratos[0]);
          setContratoSelectDisabled(true);
        } else {
          setContratoId(null);
          setContratoSelectDisabled(false);
        }
      } else {
        throw new Error(response.message || 'Erro ao carregar dados');
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados de renegociação');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCalcularJurosMulta = () => {
    let totalAcrescimo = 0;
    invoices.forEach((inv) => {
      const { juros, multa } = calcularJurosMulta(
        inv.valor + (inv.acrescimo || 0) - (inv.desconto || 0),
        inv.data_vencimento,
        dataVencimento,
        configuracao.juros,
        configuracao.multa
      );
      totalAcrescimo += juros + multa;
    });
    const valorArredondado = Math.round(totalAcrescimo * 100) / 100;
    setAcrescimo(valorArredondado);
    setAcrescimoFormatado(formatarMoeda(valorArredondado));
  };

  const handleValorParcelasChange = (valor: string) => {
    const formatado = formatarValorOnInput(valor);
    setValorParcelasFormatado(formatado);
    setValorParcelas(parsearMoeda(formatado));
  };

  const handleAcrescimoChange = (valor: string) => {
    const formatado = formatarValorOnInput(valor);
    setAcrescimoFormatado(formatado);
    setAcrescimo(parsearMoeda(formatado));
  };

  const handleDescontoChange = (valor: string) => {
    const formatado = formatarValorOnInput(valor);
    setDescontoFormatado(formatado);
    setDesconto(parsearMoeda(formatado));
  };

  const handleGerar = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await invoiceService.renegociarFaturas({
        ids: invoiceIds,
        contrato_id: contratoId || undefined,
        data_vencimento: dataVencimento,
        valor: valorParcelas,
        acrescimo,
        desconto,
        gateway_id: gatewayId || undefined,
        cobranca_id: cobrancaId || undefined,
      });
      if (response.success) {
        onSuccess?.();
        onClose();
      } else {
        throw new Error(response.message);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao renegociar faturas');
      onError?.(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(0);
    setError(null);
    setInvoices([]);
    setDataVencimento(getCurrentDate());
    setValorParcelas(0);
    setValorParcelasFormatado('0,00');
    setAcrescimo(0);
    setAcrescimoFormatado('0,00');
    setDesconto(0);
    setDescontoFormatado('0,00');
    setContratoId(null);
    setContratoSelectDisabled(false);
    setGatewayId(null);
    onClose();
  };

  const steps = ['Faturas', 'Configuração', 'Gerar'];

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), py: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <RenegotiateIcon color="primary" />
            <Typography variant="h6" fontWeight={800}>Renegociar Dívida da Cobrança</Typography>
          </Stack>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, pt: 2 }}>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && invoices.length === 0 ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {step === 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Faturas Selecionadas para Renegociação ({invoices.length})
                </Typography>
                <TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, maxHeight: 250 }}>
                  <Table size="small" stickyHeader>
                    <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Vencimento</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Valor</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoices.map((inv) => (
                        <TableRow key={inv.id}>
                          <TableCell>#{inv.id}</TableCell>
                          <TableCell>{formatarData(inv.data_vencimento)}</TableCell>
                          <TableCell>{formatarValor(inv.valor + (inv.acrescimo || 0) - (inv.desconto || 0))}</TableCell>
                          <TableCell>{inv.descricao}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {step === 1 && (
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Nova Data de Vencimento"
                    type="date"
                    fullWidth
                    value={dataVencimento}
                    onChange={(e) => setDataVencimento(e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <GatewaySelect
                    value={gatewayId ?? ''}
                    size="small"
                    fullWidth
                    onChange={(id, gw) => {
                      setGatewayId(id);
                      setSelectedGateway(gw ?? null);
                    }}
                    label="Gateway de Pagamento"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Valor das Faturas"
                    fullWidth
                    value={valorParcelasFormatado}
                    onChange={(e) => handleValorParcelasChange(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      label="Acréscimo"
                      fullWidth
                      value={acrescimoFormatado}
                      onChange={(e) => handleAcrescimoChange(e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                      }}
                    />
                    <Button
                      variant="outlined"
                      onClick={handleCalcularJurosMulta}
                      size="small"
                      sx={{ height: 40, whiteSpace: 'nowrap' }}
                      startIcon={<CalculateIcon />}
                    >
                      Calcular
                    </Button>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Desconto na Renegociação"
                    fullWidth
                    value={descontoFormatado}
                    onChange={(e) => handleDescontoChange(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  {clientId && (
                    <ContractsActiveSelect
                      clientId={clientId}
                      value={contratoId}
                      onChange={(v) => setContratoId(typeof v === 'number' ? v : null)}
                      label="Contrato Principal"
                      size="small"
                      fullWidth
                      disabled={contratoSelectDisabled}
                    />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.05), borderRadius: 2, border: `1px dashed ${theme.palette.success.main}` }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" fontWeight={700}>Valor Total Renegociado:</Typography>
                      <Typography variant="h5" fontWeight={900} color="success.main">
                        {formatarValor(valorTotal)}
                      </Typography>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            )}

            {step === 2 && (
              <Box display="flex" flexDirection="column" alignItems="center" py={2}>
                <AccountBalanceWallet color="primary" sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" fontWeight={800} gutterBottom>Resumo da Renegociação</Typography>
                <Card sx={{ minWidth: 350, borderRadius: 3, border: `1px solid ${theme.palette.divider}` }} elevation={0}>
                  <CardContent>
                    <Stack spacing={1.5}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Vencimento:</Typography>
                        <Typography variant="body2" fontWeight={700}>{formatarData(dataVencimento)}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Gateway:</Typography>
                        <Typography variant="body2" fontWeight={700}>{selectedGateway?.nome || 'Manual'}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">Faturas canceladas:</Typography>
                        <Typography variant="body2" fontWeight={700}>{invoiceIds.length}</Typography>
                      </Box>
                      <Box sx={{ my: 1, borderTop: `1px dashed ${theme.palette.divider}` }} />
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="subtitle1" fontWeight={800}>Valor da Nova Fatura:</Typography>
                        <Typography variant="subtitle1" fontWeight={900} color="primary.main">{formatarValor(valorTotal)}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  Ao confirmar, as faturas selecionadas serão CANCELADAS e uma nova fatura será gerada <br /> vinculada a esta cobrança.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2, bgcolor: alpha(theme.palette.divider, 0.03) }}>
        <Button onClick={handleClose} disabled={loading}>Cancelar</Button>
        <Box sx={{ flexGrow: 1 }} />
        {step > 0 && (
          <Button onClick={() => setStep((s) => s - 1)} disabled={loading}>
            Voltar
          </Button>
        )}
        {step < 2 ? (
          <Button variant="contained" onClick={() => setStep((s) => s + 1)} disabled={loading || invoices.length === 0}>
            Avançar
          </Button>
        ) : (
          <Button 
            variant="contained" 
            onClick={handleGerar} 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
            sx={{ px: 4, fontWeight: 800 }}
          >
            Confirmar Renegociação
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RenegociarCobrancaModal;
